import React, { useEffect, useRef, useState } from "react";

const WhyChoose = () => {
  const sectionRef = useRef(null);
  const [startCount, setStartCount] = useState(false);

  // Counter function
  const useCounter = (target, start = 0, duration = 2000) => {
    const [count, setCount] = useState(start);

    useEffect(() => {
      if (!startCount) return; // Only run when visible

      let startTimestamp = null;

      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);

        setCount(Math.floor(progress * (target - start) + start));

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };

      requestAnimationFrame(step);
    }, [target, duration, start, startCount]);

    return count;
  };

  // Intersection Observer to start animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStartCount(true);
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
  }, []);

  const projectCount = useCounter(300);
  const clientCount = useCounter(200);
  const proCount = useCounter(70);
  const expCount = useCounter(15);
  return (
    <div className="  px-10 py-20">
      <div ref={sectionRef} className="px-10 py-20">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-16">

        <div className="p-6 bg-gradient-to-r from-blue-400 to-blue-800 text-white rounded-lg shadow-md text-center">
          <p className="text-4xl font-bold">{projectCount}+</p>
          <p className="mt-2">Projects Delivered</p>
        </div>

        <div className="p-6 bg-gradient-to-r from-green-400 to-green-700 text-white rounded-lg shadow-md text-center">
          <p className="text-4xl font-bold">{clientCount}+</p>
          <p className="mt-2">Clients Served</p>
        </div>

        <div className="p-6 bg-gradient-to-r from-purple-400 to-purple-700 text-white rounded-lg shadow-md text-center">
          <p className="text-4xl font-bold">{proCount}+</p>
          <p className="mt-2">Professionals</p>
        </div>

        <div className="p-6 bg-gradient-to-r from-yellow-400 to-yellow-700 text-white rounded-lg shadow-md text-center">
          <p className="text-4xl font-bold">{expCount}+</p>
          <p className="mt-2">Years of Experience</p>
        </div>

      </div>
    </div>

      <div className="text-center mb-16">
        <h3 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Why Choose Us?
        </h3>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 mt-4 max-w-2xl mx-auto">
          Partner with us for unmatched real-time communications solutions aided
          by the latest technology stacks.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        <div className="p-6  shadow-md rounded-lg hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out">
          <div className="flex items-center mb-4">
            <img
              src="\assets\icons\techIcons\icon3.png"
              alt="icon"
              className="w-10 h-10 mr-4"
            />
            <h3 className="font-bold text-md lg:text-xl ">
              End-to-End Services
            </h3>
          </div>
          <p className="sm:text-base md:text-lg lg:text-xl">
            From concept to execution, we handle every aspect of your project.
          </p>
        </div>

        <div className="p-6  shadow-md rounded-lg hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out">
          <div className="flex items-center mb-4">
            <img
              src="\assets\icons\techIcons\icon4.png"
              alt="icon"
              className="w-10 h-10 mr-4"
            />
            <h3 className="font-bold text-md lg:text-xl mb-2">
              Trusted Expertise
            </h3>
          </div>
          <p className="sm:text-base md:text-lg lg:text-xl">
            Rely on our years of experience to deliver exceptional results.
          </p>
        </div>

        <div className="p-6  shadow-md rounded-lg hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out">
          <div className="flex items-center mb-4">
            <img
              src="\assets\icons\techIcons\icon5.png"
              alt="icon"
              className="w-10 h-10 mr-4"
            />
            <h3 className="font-bold text-md lg:text-xl mb-2">
              Customized Solutions
            </h3>
          </div>
          <p className="sm:text-base md:text-lg lg:text-xl">
            Our solutions are tailored to meet your unique business
            requirements.
          </p>
        </div>
        <div className="p-6  shadow-md rounded-lg hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out">
          <div className="flex items-center mb-4">
            <img
              src="\assets\icons\techIcons\icon6.png"
              alt="icon"
              className="w-10 h-10 mr-4"
            />
            <h3 className="font-bold text-md lg:text-xl mb-2">24/7 Support</h3>
          </div>
          <p className="sm:text-base md:text-lg lg:text-xl">
            We are available around the clock to address your concerns anytime.
          </p>
        </div>
        <div className="p-6  shadow-md rounded-lg hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out">
          <div className="flex items-center mb-4">
            <img
              src="\assets\icons\techIcons\icon7.png"
              alt="icon"
              className="w-10 h-10 mr-4"
            />
            <h3 className="font-bold text-md lg:text-xl mb-2">
              100% Quality Assured
            </h3>
          </div>
          <p className="sm:text-base md:text-lg lg:text-xl">
            Experience robust, bug-free, and scalable solutions with top-notch
            quality.
          </p>
        </div>
        <div className="p-6  shadow-md rounded-lg hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out">
          <div className="flex items-center mb-4">
            <img
              src="\assets\icons\techIcons\icon8.png"
              alt="icon"
              className="w-10 h-10 mr-4"
            />
            <h3 className="font-bold text-md lg:text-xl mb-2">
              Customer-Centric Approach
            </h3>
          </div>
          <p className="sm:text-base md:text-lg lg:text-xl">
            Your satisfaction is our top priority, achieved through our
            collaborative methods.
          </p>
        </div>
        <div className="p-6  shadow-md rounded-lg hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out">
          <div className="flex items-center mb-4">
            <img
              src="\assets\icons\techIcons\icon9.png"
              alt="icon"
              className="w-10 h-10 mr-4"
            />
            <h3 className="font-bold text-md lg:text-xl mb-2">Data Security</h3>
          </div>
          <p className="sm:text-base md:text-lg lg:text-xl">
            We ensure your data remains secure and compliant with industry
            standards.
          </p>
        </div>
        <div className="p-6  shadow-md rounded-lg hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out">
          <div className="flex items-center mb-4">
            <img
              src="\assets\icons\techIcons\icon10.png"
              alt="icon"
              className="w-10 h-10 mr-4"
            />
            <h3 className="font-bold text-md lg:text-xl mb-2">
              Agile and Flexible Engagement
            </h3>
          </div>
          <p className="sm:text-base md:text-lg lg:text-xl">
            Our adaptable engagement models cater to evolving business needs.
          </p>
        </div>
        <div className="p-6  shadow-md rounded-lg hover:bg-gradient-to-r hover:from-blue-300 hover:to-green-200 hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out">
          <div className="flex items-center mb-4">
            <img
              src="\assets\icons\techIcons\icon11.png"
              alt="icon"
              className="w-10 h-10 mr-4"
            />
            <h3 className="font-bold text-md lg:text-xl mb-2">
              Innovation-Driven Solutions
            </h3>
          </div>
          <p className="sm:text-base md:text-lg lg:text-xl">
            Leverage cutting-edge technology to stay ahead in your industry.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WhyChoose;
